import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import config from '../config';

/**
 * FacilityContext
 * ----------------------------------------------------------------------------
 * Global state for the active facility selection. Used by:
 *   - <FacilitySwitcher /> in the top bar
 *   - List pages (leads, patients, appointments, ...) to scope queries
 *   - Forms that need to pre-select the current facility
 *   - Homepage dashboard stats
 *
 * State shape:
 *   - facilities             — full list of facilities the user has access to
 *   - allFacilities          — every facility on the org (admins see same as facilities; staff might see fewer)
 *   - selectedFacilityId     — number (specific facility) | 'all' (cross-facility view) | null (no facilities)
 *   - role                   — { is_owner, is_admin, is_owner_or_admin, staff_id }
 *   - loading                — initial bootstrap in flight
 *
 * Persistence: per-org localStorage key `facility:selected:{orgId}`. So a user
 * with multiple orgs remembers a separate selection per org.
 *
 * Helpers exposed:
 *   - currentFacility        — full facility object for the selected id (null when 'all')
 *   - apiParams()            — returns `{ facility_id: <id> }` when scoped, `{}` when 'all'
 *   - isMultiFacility        — true when org has >1 facility (controls whether to show the switcher)
 *   - canSelectAll           — true for owners/admins (only they get the cross-facility view)
 *   - selectFacility(id)     — call to switch
 *   - selectAll()            — call to choose 'all'
 *   - reload()               — refetch (useful after user adds a new facility)
 */

const FacilityContext = createContext(null);

export const useFacility = () => {
    const ctx = useContext(FacilityContext);
    if (!ctx) {
        // Returning a permissive default lets non-Provider-wrapped pages
        // (login screen, etc.) call useFacility without crashing.
        return DEFAULT_CTX;
    }
    return ctx;
};

const DEFAULT_CTX = {
    facilities: [],
    allFacilities: [],
    selectedFacilityId: null,
    role: { is_owner: false, is_admin: false, is_owner_or_admin: false, staff_id: null },
    loading: false,
    currentFacility: null,
    isMultiFacility: false,
    canSelectAll: false,
    apiParams: () => ({}),
    selectFacility: () => {},
    selectAll: () => {},
    reload: () => {}
};

const lsKey = (orgId) => `facility:selected:${orgId || 'unknown'}`;

export const FacilityProvider = ({ children }) => {
    const [data, setData] = useState({
        facilities: [],
        allFacilities: [],
        selectedFacilityId: null,
        role: { is_owner: false, is_admin: false, is_owner_or_admin: false, staff_id: null },
        organizationId: null
    });
    const [loading, setLoading] = useState(true);

    const fetchContext = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) { setLoading(false); return; }

        try {
            const res = await axios.get(`${config.API_BASE_URL}/api/facilities/context`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.data?.success) {
                setLoading(false);
                return;
            }
            const ctx = res.data.data;
            const accessibleSet = new Set(ctx.accessible_facility_ids || []);
            const visibleFacilities = ctx.facilities.filter(f => accessibleSet.has(f.id));

            // Restore previous selection from localStorage if it's still valid
            let selected = null;
            try {
                const saved = localStorage.getItem(lsKey(ctx.organization_id));
                if (saved === 'all' && ctx.role.is_owner_or_admin) {
                    selected = 'all';
                } else if (saved && saved !== 'all') {
                    const savedNum = Number(saved);
                    if (accessibleSet.has(savedNum)) selected = savedNum;
                }
            } catch (_) { /* ignore */ }

            // Fall back to the server-suggested default
            if (selected === null) selected = ctx.default_selection;

            setData({
                facilities: visibleFacilities,
                allFacilities: ctx.facilities,
                selectedFacilityId: selected,
                role: ctx.role,
                organizationId: ctx.organization_id
            });
        } catch (e) {
            console.warn('[FacilityContext] fetch failed:', e.response?.status || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchContext();
    }, [fetchContext]);

    // Refresh on login / clear on logout. The provider stays mounted across
    // login transitions, so we wire window events to keep it in sync with the
    // authenticated user. Login.js dispatches 'auth:login' after setting the
    // token; Dashboard's handleLogout dispatches 'auth:logout' before navigate.
    useEffect(() => {
        const onLogin = () => fetchContext();
        const onLogout = () => {
            setData({
                facilities: [],
                allFacilities: [],
                selectedFacilityId: null,
                role: { is_owner: false, is_admin: false, is_owner_or_admin: false, staff_id: null },
                organizationId: null
            });
            setLoading(false);
        };
        window.addEventListener('auth:login', onLogin);
        window.addEventListener('auth:logout', onLogout);
        // Cross-tab sync: 'storage' events fire in OTHER tabs only, so when a
        // user logs in/out elsewhere we still pick it up.
        const onStorage = (e) => {
            if (e.key === 'token') {
                if (e.newValue) fetchContext();
                else onLogout();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('auth:login', onLogin);
            window.removeEventListener('auth:logout', onLogout);
            window.removeEventListener('storage', onStorage);
        };
    }, [fetchContext]);

    const persist = useCallback((orgId, value) => {
        try {
            if (value === null || value === undefined) {
                localStorage.removeItem(lsKey(orgId));
            } else {
                localStorage.setItem(lsKey(orgId), String(value));
            }
        } catch (_) { /* ignore */ }
    }, []);

    const selectFacility = useCallback((id) => {
        const numId = Number(id);
        setData(prev => {
            const next = { ...prev, selectedFacilityId: numId };
            persist(prev.organizationId, numId);
            return next;
        });
    }, [persist]);

    const selectAll = useCallback(() => {
        setData(prev => {
            if (!prev.role.is_owner_or_admin) return prev; // staff can't select All
            const next = { ...prev, selectedFacilityId: 'all' };
            persist(prev.organizationId, 'all');
            return next;
        });
    }, [persist]);

    const value = useMemo(() => {
        const isMultiFacility = data.allFacilities.length > 1;
        const canSelectAll = !!data.role.is_owner_or_admin && isMultiFacility;
        const currentFacility = data.selectedFacilityId === 'all' || data.selectedFacilityId == null
            ? null
            : data.allFacilities.find(f => f.id === data.selectedFacilityId) || null;

        const apiParams = () => {
            if (data.selectedFacilityId == null) return {};
            if (data.selectedFacilityId === 'all') return {};
            return { facility_id: data.selectedFacilityId };
        };

        return {
            ...data,
            loading,
            currentFacility,
            isMultiFacility,
            canSelectAll,
            apiParams,
            selectFacility,
            selectAll,
            reload: fetchContext
        };
    }, [data, loading, selectFacility, selectAll, fetchContext]);

    return <FacilityContext.Provider value={value}>{children}</FacilityContext.Provider>;
};

export default FacilityContext;
